/**
 * Merge reportDaily bucket documents and derive UI structures.
 * Bucket docs come from Firestore reportDaily collection (dateKey, totals, dailyPaid, byClient, byService, byLash).
 */

/**
 * Convert YYYY-MM-DD to YYYY/MM/DD for display consistency with existing report.
 * @param {string} dateKey
 * @returns {string}
 */
function dateKeyToSlash(dateKey) {
  return String(dateKey || '').replace(/-/g, '/');
}

/**
 * Merge multiple reportDaily bucket documents into one structure.
 * @param {Array} bucketDocs
 * @returns {Object}
 */
export function mergeReportBuckets(bucketDocs) {
  const totals = { expected: 0, paid: 0, fees: 0, net: 0 };
  const dailyValues = [];
  const byClient = {};
  const byService = {};
  const byLash = {};
  const byPaymentMethod = {};
  const monthData = {};

  for (const doc of bucketDocs || []) {
    const d = doc.data ? doc.data() : doc;
    const dateKey = d.dateKey || doc.id || '';

    totals.expected += Number(d.totals?.expected) || 0;
    totals.paid += Number(d.totals?.paid) || 0;
    totals.fees += Number(d.totals?.fees) || 0;
    totals.net += Number(d.totals?.net) || 0;

    const dailyPaid = Number(d.dailyPaid) || 0;
    const dailyFee = Number(d.dailyFee) || 0;
    const dailyNet = Number(d.dailyNet) || 0;
    if (dailyPaid > 0) {
      dailyValues.push({
        date: dateKeyToSlash(dateKey),
        gross: dailyPaid,
        fee: dailyFee,
        net: dailyNet,
      });
    }

    for (const [cid, v] of Object.entries(d.byClient || {})) {
      if (!byClient[cid]) {
        byClient[cid] = {
          expected: 0,
          paid: 0,
          count: 0,
          unpaidExpected: 0,
          unpaidCount: 0
        };
      }
      byClient[cid].expected += Number(v.expected) || 0;
      byClient[cid].paid += Number(v.paid) || 0;
      byClient[cid].count += Number(v.count) || 0;
      byClient[cid].unpaidExpected += Number(v.unpaidExpected) || 0;
      byClient[cid].unpaidCount += Number(v.unpaidCount) || 0;
    }

    for (const [sid, v] of Object.entries(d.byService || {})) {
      if (!byService[sid]) byService[sid] = { revenue: 0, fee: 0, net: 0, count: 0 };
      byService[sid].revenue += Number(v.revenue) || 0;
      byService[sid].fee += Number(v.fee) || 0;
      byService[sid].net += Number(v.net) || 0;
      byService[sid].count += Number(v.count) || 0;
    }

    for (const [lid, v] of Object.entries(d.byLash || {})) {
      if (!byLash[lid]) byLash[lid] = { expected: 0, count: 0 };
      byLash[lid].expected += Number(v.expected) || 0;
      byLash[lid].count += Number(v.count) || 0;
    }

    for (const [mid, v] of Object.entries(d.byPaymentMethod || {})) {
      if (!byPaymentMethod[mid]) {
        byPaymentMethod[mid] = { gross: 0, fee: 0, net: 0, count: 0 };
      }
      byPaymentMethod[mid].gross += Number(v.gross) || 0;
      byPaymentMethod[mid].fee += Number(v.fee) || 0;
      byPaymentMethod[mid].net += Number(v.net) || 0;
      byPaymentMethod[mid].count += Number(v.count) || 0;
    }

    const monthKey = dateKey.slice(0, 7).replace('-', '/');
    if (!monthData[monthKey]) monthData[monthKey] = { total: 0, paid: 0 };
    monthData[monthKey].total += Number(d.totals?.expected) || 0;
    monthData[monthKey].paid += Number(d.totals?.paid) || 0;
  }

  dailyValues.sort((a, b) => (a.date > b.date ? -1 : 1));

  return { totals, dailyValues, byClient, byService, byLash, byPaymentMethod, monthData };
}

/**
 * Derive full report UI structures from merged buckets and lookup maps.
 * @param {{ totals, dailyValues, byClient, byService, byLash, byPaymentMethod, monthData }} merged
 * @param {Object} clients - clientId -> { name }
 * @param {Object} services - serviceId -> { name }
 * @param {Object} users - userId -> { name }
 * @param {Object} [paymentMethods] - paymentMethodId -> { name }
 */
export function mergedToReportUI(merged, clients, services, users, paymentMethods = {}) {
  const customerPaidVsExpected = Object.entries(merged.byClient)
    .filter(([, v]) => v.expected > 0)
    .map(([clientId, v]) => ({
      clientId,
      clientName: clients[clientId]?.name ?? 'Não encontrado',
      expected: v.expected,
      paid: v.paid,
      difference: v.paid - v.expected,
      count: v.count
    }))
    .sort((a, b) => a.clientName.localeCompare(b.clientName));

  const outstandingByCustomer = Object.entries(merged.byClient)
    .filter(([, v]) => v.unpaidExpected != 0)
    .map(([clientId, v]) => ({
      clientId,
      clientName: clients[clientId]?.name ?? 'Não encontrado',
      expected: -v.unpaidExpected,
      count: v.unpaidCount
    }))
    .sort((a, b) => a.clientName.localeCompare(b.clientName));

  const revenueByService = Object.entries(merged.byService)
    .filter(([, v]) => v.revenue > 0)
    .map(([serviceId, v]) => ({
      serviceId,
      serviceName: services[serviceId]?.name ?? 'Não encontrado',
      gross: v.revenue,
      fee: v.fee,
      net: v.net,
      count: v.count
    }))
    .sort((a, b) => b.gross - a.gross);

  const revenueByTechnician = Object.entries(merged.byLash)
    .map(([lashId, v]) => {
      const percent = users[lashId]?.percentToPay;
      const pct = percent != null && Number.isFinite(Number(percent))
        ? Math.max(0, Math.min(100, Math.round(Number(percent))))
        : 100;
      return {
        lashId,
        lashName: users[lashId]?.name ?? 'Não encontrado',
        expected: v.expected,
        count: v.count,
        payToProvider: v.expected * (pct / 100)
      };
    })
    .sort((a, b) => b.expected - a.expected);

  const revenueByPaymentMethod = Object.entries(merged.byPaymentMethod || {})
    .filter(([, v]) => v.gross > 0)
    .map(([paymentMethodId, v]) => ({
      paymentMethodId,
      paymentMethodName: paymentMethods[paymentMethodId]?.name ?? 'Não encontrado',
      gross: v.gross,
      fee: v.fee,
      net: v.net,
      count: v.count
    }))
    .sort((a, b) => b.gross - a.gross);

  const categories = Object.keys(merged.monthData).sort();
  const hasData = categories.length > 0;
  const series = hasData
    ? [
        { name: 'Total', data: categories.map((k) => merged.monthData[k].total) },
        { name: 'Pago', data: categories.map((k) => merged.monthData[k].paid) }
      ]
    : [{ name: 'Total', data: [] }, { name: 'Pago', data: [] }];
  const chartData = {
    categories: hasData ? categories : ['Sem dados'],
    series
  };

  return {
    totals: merged.totals,
    dailyValues: merged.dailyValues,
    customerPaidVsExpected,
    outstandingByCustomer,
    revenueByService,
    revenueByTechnician,
    revenueByPaymentMethod,
    chartData
  };
}
